import { useRef, type ReactNode } from "react";
import { usePageScript } from "../shop/page-script";

/** Invoker command attributes, which React passes through as written. */
export const command = (target: string, action: "show-modal" | "close") =>
  ({ commandfor: target, command: action }) as Record<string, string>;

/**
 * A modal dialog on the native <dialog>. Open it from any button
 * with `{...command(id, "show-modal")}`. The kit's script (site/src/ui/dialog.ts) adds invoker
 * commands where a browser lacks them, closing on a backdrop press and dragging a sheet down.
 */
export function Dialog({
  id,
  title,
  eyebrow,
  media,
  description,
  variant = "center",
  size = "md",
  closeLabel = "Close",
  onClose,
  className,
  footer,
  children,
}: {
  id: string;
  title: string;
  /** A short line above the title, as a popup names the site. */
  eyebrow?: string;
  /** A picture beside the words (under them on a phone), as a signup popup sets one. */
  media?: { src: string; alt: string };
  /** Shown under the title and read with it. */
  description?: string;
  variant?: "center" | "sheet" | "side";
  size?: "sm" | "md" | "lg";
  closeLabel?: string;
  /** Keep a controlled consumer in sync with native Escape, backdrop and close commands. */
  onClose?: () => void;
  className?: string;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const root = useRef<HTMLDialogElement>(null);
  usePageScript(root, async () => {
    const { mountDialog } = await import("../../../src/ui/dialog");
    if (root.current) mountDialog(root.current);
  });
  return (
    <dialog
      ref={root}
      id={id}
      className={["ui-dialog", className].filter(Boolean).join(" ")}
      data-ui="dialog"
      data-variant={variant}
      data-size={size}
      data-media={media ? "" : undefined}
      onClose={onClose}
      aria-labelledby={`${id}-title`}
      aria-describedby={description ? `${id}-description` : undefined}
      {...({ closedby: "any" } as Record<string, string>)}
    >
      {variant === "sheet" && <div className="ui-dialog-handle" aria-hidden="true" />}
      <header className="ui-dialog-header">
        {eyebrow ? (
          <div className="ui-dialog-heading">
            <p className="ui-dialog-eyebrow">{eyebrow}</p>
            <h2 id={`${id}-title`} className="ui-dialog-title">
              {title}
            </h2>
          </div>
        ) : (
          <h2 id={`${id}-title`} className="ui-dialog-title">
            {title}
          </h2>
        )}
        <button
          type="button"
          className="ui-dialog-close"
          aria-label={closeLabel}
          {...command(id, "close")}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M6 6l12 12M18 6 6 18"></path>
          </svg>
        </button>
      </header>
      {description && (
        <p id={`${id}-description`} className="ui-dialog-description">
          {description}
        </p>
      )}
      <div className="ui-dialog-body">{children}</div>
      {footer && <footer className="ui-dialog-footer">{footer}</footer>}
      {media && (
        <div className="ui-dialog-media">
          <img src={media.src} alt={media.alt} decoding="async" />
        </div>
      )}
    </dialog>
  );
}
